import Vue from 'vue'
import Vuex from 'vuex'

import * as util from '@shared/user-util'
import * as C from '@shared/constants'
import * as M from './mutations'
import * as _ from '@/lodash'
import suggestions from './modules/suggestions/suggestion.module'
import emotes from './modules/emotes/emote.module'
import twitch from './modules/twitch/twitch.module'
import notifications from './modules/notifications/notification.module'
import api from '@/api'

Vue.use(Vuex)

const defaultChannel = util.defaultChannelState()

const state = {
    user: { // The current viewer
        id: null,
        opaqueId: '',
        name: '',
        profileImg: '',
        role: null,
    },
    channel: { ...defaultChannel },
    // Enables the "save changes" feature
    lastSavedChannel: { ...defaultChannel },
    isLoadingChannel: false,
    fontLoadingState: C.STATE_DEFAULT,
    titleFontLoadingState: C.STATE_DEFAULT,
    headerImgLoadingState: C.STATE_DEFAULT,
    isPlannedDowntime: false,
    showIntroTutorial: false,
    // Coordinates global animation states that trigger on a new list render.
    // I.e., whenever a user changes the listType, or sortDir.
    listRenderCount: 0,
}

export const mutations = {
    [M.UPDATE_USER] (state, { user }) {
        for (const [key, val] of Object.entries(user)) {
            Vue.set(state.user, key, val)
        }
    },
    // Use Object.assign b/c channel is partially updated a lot.
    [M.UPDATE_CHANNEL] (state, { channel }) {
        // Ensures that any new keys, like "hasOAuthToken",
        // are made reactive. I didn't want to include "hasOAuthToken"
        // in util.defaultChannelState() because... it didn't feel right.
        for (const [key, val] of Object.entries(channel)) {
            Vue.set(state.channel, key, val)
        }
    },
    [M.UPDATE_LAST_SAVED_CHANNEL] (state, { channel }) {
        for (const [key, val] of Object.entries(channel)) {
            Vue.set(state.lastSavedChannel, key, val)
        }
    },
    [M.TOGGLE_LOADING_CHANNEL] (state, { isLoading }) {
        state.isLoadingChannel = isLoading
    },
    [M.SET_FONT_LOADING_STATE] (state, { state: fontState }) {
        state.fontLoadingState = fontState
    },
    [M.SET_TITLE_FONT_LOADING_STATE] (state, { state: fontState }) {
        state.titleFontLoadingState = fontState
    },
    [M.SET_HEADER_IMAGE_LOADING_STATE] (state, { state: imgState }) {
        state.headerImgLoadingState = imgState
    },
    [M.SET_PLANNED_DOWNTIME] (state) {
        state.isPlannedDowntime = true
    },
    [M.TOGGLE_BROADCASTER_INTRO_TUTORIAL] (state, { show }) {
        state.showIntroTutorial = show
    },
    [M.INCREMENT_LIST_RENDER_COUNT] (state) {
        state.listRenderCount++
    },
}

export const actions = {
    async fetchChannel ({ state, commit, dispatch }) {
        commit(M.TOGGLE_LOADING_CHANNEL, { isLoading: true })

        try {
            const { channelId } = state.channel
            const res = await api.get(`channels/${channelId}`)
            const { channel } = res.data

            commit(M.UPDATE_CHANNEL, { channel })
            commit(M.UPDATE_LAST_SAVED_CHANNEL, { channel })

            // This will trigger a fetchSuggestions action, now that the
            // default listType is known.
            commit(M.SET_LIST_FILTERS, { listType: channel.listType })
        } finally {
            commit(M.TOGGLE_LOADING_CHANNEL, { isLoading: false })
        }
    },
    async updateChannel ({ getters, commit }, { channel }) {
        const { channelId } = getters
        await api.patch(`channels/${channelId}`, channel)

        commit(M.UPDATE_CHANNEL, { channel })
        commit(M.UPDATE_LAST_SAVED_CHANNEL, { channel })
    },
    async fetchUser ({ state, commit, getters }) {
        const { channelId } = state.channel
        const { userId } = getters

        const res = await api.get(`channels/${channelId}/users/${userId}`)
        const { user } = res.data

        // Merge data from jwt & helix
        const fullUser = { ...state.user, ...user }

        commit(M.UPDATE_USER, { user: fullUser })
        commit(M.SET_USERS_LAST_SUGGESTION, { suggestion: user.lastSuggestion })

        Sentry.setUser({
            id: fullUser.id,
            username: fullUser.name
        })
    },
}

export const getters = {
    channelId: ({ channel }) => channel.channelId,
    // Use channelName for display purposes
    channelName: ({ channel }) => channel.channelName,
    // Use channelLogin to generate urls
    channelLogin: ({ channel }) => {
        // 2020-06-21: I fucked up by using channelName for urls instead of
        // channelLogin. Fallback to channelName until the database is updated
        // with the login, which will take months. So keep this fallback forever.
        return channel.channelLogin || channel.channelName
    },
    revealLinkPreview: ({ channel }) => channel.revealLinkPreview,
    requiresOAuthSetup: ({ channel }) => !channel.hasOAuthToken,
    messageToViewer: ({ channel }) => channel.messageToViewer,
    // Hacky way to simulate a user object, since the channel IS the broadcasterUser
    broadcasterUser: ({ channel: c }) => ({
        name: c.channelName, login: c.channelLogin, profileImg: c.profileImg
    }),
    userId: ({ user }) => user?.id, // Only identity linked users have a user.id
    isAnonymousUser: ({ user }) => util.isAnonymousUser(user),
    isOpaqueUser: ({ user }) => util.isOpaqueUser(user),
    isIdentityLinkedUser: ({ user }) => util.isIdentityLinkedUser(user),
    isModerator: (s, g) => util.isModeratorOfChannel(s.user, g.channelId),
    isBroadcaster: (s, g) => util.isBroadcasterOfChannel(s.user, g.channelId),
    isAdmin: ({ user, channel }) => util.isSuggestionBoxAdmin(user, channel),
    isLoadingChannel: state => state.isLoadingChannel,
    isGuanzoUser: ({ user }) => user.id === GUANZO_CHANNEL_ID,
    isGuanzoSBox: ({ channel }) => channel.channelId === GUANZO_CHANNEL_ID,
    // The main UI is basically a big list of components.
    masterList: (state, getters) => {
        const suggestions = state.suggestions.data
        // In the future, the masterList will be composed of multiple lists.
        // Right now, it's just the suggestion list
        let masterList = suggestions
        // Move notifications to the top of the list
        masterList = _.orderBy(masterList, ['isNotification'], ['desc'])
        return masterList
    }
}

export default new Vuex.Store({
    strict: process.env.NODE_ENV !== 'production',
    state,
    mutations,
    actions,
    getters,
    modules: {
        suggestions,
        emotes,
        twitch,
        notifications,
    }
})
